import { useFormik } from "formik";
import InputField from "../../ui/form/InputField";
import { RetirementBenefitValidation } from "../../../validation/formValidation";
import usePost from "../../../hooks/api/usePost";
import apiRequest from "../../../utils/apiRequest";
import FileInputField from "../../ui/form/FileInputField";
import Button from "../../ui/Button";
import React, { useState } from "react";
import { formatDateForInput } from "../../../utils/helper";
import SelectField from "../../ui/form/SelectField";
import axios from "axios";
import handleApiError from "./handleApiError";
import authManager from "../../../utils/authManager";
import toast from "react-hot-toast";
import FullScreenLoader from "../../ui/FullScreenLoader";
import PaymentOptions from "./PaymentOptions";
import { DASHBOARD_ROUTE, HOME_ROUTE } from "../../../routes/web/rootRoutes";
import { useNavigate } from "react-router-dom";

const RetirementBenefitForm = ({ data = {} }) => {
  const [errors, setErrors] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [submissionResponse, setSubmissionResponse] = useState(null);
  const [submittedFormData, setSubmittedFormData] = useState(null);
  const navigate = useNavigate(); 
  const userString = localStorage.getItem('user');
  let union_code;
  if (userString) {
    const user = JSON.parse(userString);
    union_code = user.union_code;
  }

  const {
    post,
    isLoading: postLoading,
    error,
    serverErrors,
  } = usePost(apiRequest.createForm);

  const formik = useFormik({
    validationSchema: RetirementBenefitValidation,

    initialValues: {
      // Member Details
      member_no: data?.member_no || union_code || "",
      date: data?.date || formatDateForInput(new Date()),
      member_name: data?.member_name || "",
      designation: data?.designation || "",
      married_unmarried: data?.married_unmarried || "",
      addhar_no: data?.addhar_no || "",
      address: data?.address || "",
      mobile: data?.mobile || "",
      email: data?.email || "",
      dob: data?.dob && formatDateForInput(data?.dob) || "",

      // Nominee Details
      nominee_name: data?.nominee_name || "",
      nominee_relationship: data?.nominee_relationship || "",

      // File Uploads
      photo: null,
      member_card: null,
      nominee_photo: null,
      subscription: null,
      member_signature: null,
      document: null,

      // Agreement
      agree: false,
    },

    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      
      try {
        const formData = new FormData();

        // Append all text fields
        const textFields = {
          member_no: values.member_no,
          date: values.date,
          member_name: values.member_name,
          designation: values.designation,
          married_unmarried: values.married_unmarried,
          addhar_no: values.addhar_no,
          address: values.address,
          mobile: values.mobile,
          email: values.email,
          dob: values.dob,
          nominee_name: values.nominee_name,
          nominee_relationship: values.nominee_relationship,
        };

        Object.entries(textFields).forEach(([key, value]) => {
          if (value) formData.append(key, value.toString());
        });

        // Append files with correct backend field names
        const fileFields = {
          member_photo: values.photo,
          member_member_card: values.member_card,
          member_nominee_photo: values.nominee_photo,
          member_subscription: values.subscription,
          member_member_signature: values.member_signature,
          member_document: values.document,
        };

        Object.entries(fileFields).forEach(([key, file]) => {
          if (file) {
            formData.append(key, file);
            console.log(`📎 Attached file: ${key}`, file.name);
          }
        });

        // Debug log
        console.log("Submitting Retirement Benefit Form:");
        for (let [key, val] of formData.entries()) {
          console.log(`${key}:`, val instanceof File ? `File: ${val.name}` : val);
        }

        const token = authManager.getAccessToken();
        if (!token) {
          toast.error("Authentication token not found. Please login again.");
          setIsSubmitting(false);
          return;
        }

        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) {
          toast.error("API URL not configured");
          setIsSubmitting(false);
          return;
        }

        const response = await axios.post(
          `${apiUrl}retirement`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            timeout: 30000,
          }
        );

        console.log("✅ Retirement Benefit Form submitted:", response.data);
        
        setSubmissionResponse(response.data);
        setSubmittedFormData(values);
        
        toast.success("Retirement Benefit form submitted successfully!");
        resetForm();

        setTimeout(() => {
              navigate(DASHBOARD_ROUTE);

          setIsSubmitting(false);
          setShowPayment(true);
        }, 1500);

      } catch (error) {
        console.error("Submission error:", error);
        
        if (error.response) {
          console.error("Server Error:", error.response.data);
          toast.error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          console.error("No response from server");
          toast.error("No response from server. Please check your internet connection.");
        } else {
          console.error("Request error:", error.message);
          toast.error(`Error: ${error.message}`);
        }
        
        handleApiError(error, setErrors, "Failed to submit Retirement Benefit form");
        setIsSubmitting(false);
      }
    },
  });

  const setFieldValue = (field, value) => {
    formik.setFieldValue(field, value);
    formik.setFieldTouched(field, true);
  };

  const handleClosePayment = () => {
    setShowPayment(false);
    window.location.href = HOME_ROUTE;
  };

  if (showPayment) {
    return (
      <>
        {showPayment && (
          <PaymentOptions
            onClose={handleClosePayment}
          />
        )}
      </>
    );
  }

  return (
    <>
      {isSubmitting && (
        <FullScreenLoader message="Submitting retirement benefit application..." />
      )}

      <div className="max-w-7xl mx-auto p-6 bg-gradient-to-br min-h-screen">
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <div className="bg-gradient-to-r from-violet-500 to-violet-900 text-white p-6">
            <h1 className="text-center text-3xl font-bold mb-2">
              REGISTRATION (RETIREMENT BENEFIT SCHEME)
            </h1>
          </div>

          <form onSubmit={formik.handleSubmit} className="p-8">
            {/* Section 1: Member Number & Date */}
            <div className="mb-8 p-6 bg-gray-50 rounded-lg border-l-4 border-violet-500">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Member No. <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="member_no"
                    placeholder="Enter Membership No"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.member_no}
                    error={formik.touched.member_no && formik.errors.member_no}
                    serverError={serverErrors?.member_no}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="date"
                    type="date"
                    placeholder="Select Date"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.date}
                    error={formik.touched.date && formik.errors.date}
                    serverError={serverErrors?.date}
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Address Section */}
            <div className="mb-8 p-6 bg-blue-50 rounded-lg border-l-4 border-blue-500">
              <h3 className="text-xl font-semibold text-gray-800 mb-1">To,</h3>
              <h3 className="text-lg font-medium text-gray-700 mb-1">The President / General Secretary</h3>
              <h3 className="text-lg font-medium text-gray-700">Association of Cine & Television Art Directors & Costume Designers</h3>
            </div>

            {/* Section 3: Member Personal Details */}
            <div className="mb-8 p-6 bg-green-50 rounded-lg border-l-4 border-green-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">👤</span> Member Personal Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Member's Name <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="member_name"
                    placeholder="Enter Member Name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.member_name}
                    error={formik.touched.member_name && formik.errors.member_name}
                    serverError={serverErrors?.member_name}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Designation <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="designation"
                    placeholder="Enter designation"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.designation}
                    error={formik.touched.designation && formik.errors.designation}
                    serverError={serverErrors?.designation}
                  />
                </div>

                <div className="space-y-2">
                  <SelectField
                    options={[
                      { value: 'Married', label: 'Married' },
                      { value: 'UnMarried', label: 'UnMarried' },
                    ]}
                    label="Marital Status <span className='text-red-500'>*</span>"
                    id="married_unmarried"
                    value={formik.values.married_unmarried}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.married_unmarried && formik.errors.married_unmarried}
                    serverError={serverErrors?.married_unmarried}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Aadhar No <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="addhar_no"
                    placeholder="Enter Aadhar No"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.addhar_no}
                    error={formik.touched.addhar_no && formik.errors.addhar_no}
                    serverError={serverErrors?.addhar_no}
                  />
                </div>

                <div className="space-y-2 lg:col-span-2">
                  <label className="block font-semibold text-gray-700">
                    Residential Address <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="address"
                    placeholder="Enter complete address"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.address}
                    error={formik.touched.address && formik.errors.address}
                    serverError={serverErrors?.address}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Contact No. <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="mobile"
                    placeholder="Enter mobile number"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.mobile}
                    error={formik.touched.mobile && formik.errors.mobile}
                    serverError={serverErrors?.mobile}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Email ID <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="email"
                    type="email"
                    placeholder="Enter email address"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.email}
                    error={formik.touched.email && formik.errors.email}
                    serverError={serverErrors?.email}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="dob"
                    type="date"
                    placeholder="Select DOB"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.dob}
                    error={formik.touched.dob && formik.errors.dob}
                    serverError={serverErrors?.dob}
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Nominee Details */}
            <div className="mb-8 p-6 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">👤</span> Nominee Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Nominee Name <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="nominee_name"
                    placeholder="Enter nominee name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.nominee_name}
                    error={formik.touched.nominee_name && formik.errors.nominee_name}
                    serverError={serverErrors?.nominee_name}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">
                    Nominee Relationship <span className="text-red-500">*</span>
                  </label>
                  <InputField
                    id="nominee_relationship"
                    placeholder="Enter relationship"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.nominee_relationship}
                    error={formik.touched.nominee_relationship && formik.errors.nominee_relationship}
                    serverError={serverErrors?.nominee_relationship}
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Documents Upload */}
            <div className="mb-8 p-6 bg-orange-50 rounded-lg border-l-4 border-orange-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📎</span> Documents Upload
                <span className="text-sm font-normal text-gray-600 ml-2">(Please upload all required documents)</span>
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Required Documents */}
                <div className="space-y-4 p-6 bg-red-50 rounded-lg border border-red-200">
                  <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center">
                    <span className="mr-2">📋</span> Required Documents
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Membership Card Copy <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="member_card"
                        onChange={(event) => {
                          setFieldValue("member_card", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.member_card && formik.errors.member_card}
                        serverError={serverErrors?.member_card}
                      />
                      <p className="text-xs text-gray-500">Upload scanned copy of membership card</p>
                    </div>

                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Nominee ID Proof <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="document"
                        onChange={(event) => {
                          setFieldValue("document", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.document && formik.errors.document}
                        serverError={serverErrors?.document}
                      />
                      <p className="text-xs text-gray-500">Aadhar/PAN/Passport/Bank Passbook of Nominee</p>
                    </div>
                  </div>
                </div>

                {/* Financial Documents */}
                <div className="space-y-4 p-6 bg-green-50 rounded-lg border border-green-200">
                  <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center">
                    <span className="mr-2">💰</span> Financial Documents
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Subscription Receipt <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="subscription"
                        onChange={(event) => {
                          setFieldValue("subscription", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.subscription && formik.errors.subscription}
                        serverError={serverErrors?.subscription}
                      />
                      <p className="text-xs text-gray-500">Current year subscription paid receipt</p>
                    </div>
                  </div>
                </div>

                {/* Photos & Signatures - Full Width */}
                <div className="space-y-4 p-6 bg-blue-50 rounded-lg border border-blue-200 lg:col-span-2">
                  <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
                    <span className="mr-2">📸</span> Photos & Signatures
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Member Photo <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="photo"
                        onChange={(event) => {
                          setFieldValue("photo", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.photo && formik.errors.photo}
                        serverError={serverErrors?.photo}
                      />
                      <p className="text-xs text-gray-500">Passport size photo</p>
                    </div>

                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Nominee Photo <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="nominee_photo"
                        onChange={(event) => {
                          setFieldValue("nominee_photo", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.nominee_photo && formik.errors.nominee_photo}
                        serverError={serverErrors?.nominee_photo}
                      />
                      <p className="text-xs text-gray-500">Nominee's passport size photo</p>
                    </div>

                    <div className="space-y-2">
                      <label className="block font-semibold text-gray-700">
                        Member Signature <span className="text-red-500">*</span>
                      </label>
                      <FileInputField
                        id="member_signature"
                        onChange={(event) => {
                          setFieldValue("member_signature", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.member_signature && formik.errors.member_signature}
                        serverError={serverErrors?.member_signature}
                      />
                      <p className="text-xs text-gray-500">Upload scanned signature</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 6: Eligibility Rules */}
            <div className="mb-8 p-6 bg-purple-50 rounded-lg border-l-4 border-purple-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📋</span> Eligibility Rules
              </h2>

              <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm">
                <li>Any member who has attained the age of 65 years shall be eligible for the retirement benefit.</li>
                <li className="font-semibold text-red-600">No retirement application below 65 years of age shall be accepted.</li>
                <li>Subscriptions of such members should be cleared till date. Any pending subscription shall not be eligible for retirement benefit.</li>
                <li>While submission of the retirement benefit application, the nomination details should also be furnished by the respective member.</li>
              </ul>
            </div>

            {/* Section 7: Declaration */}
            <div className="mb-8 p-6 bg-pink-50 rounded-lg border-l-4 border-pink-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📜</span> Declaration
              </h2>

              <div className="space-y-4">
                <p className="text-sm text-gray-700">
                  I hereby declare that I have attained the age of 65 years and all my subscriptions are cleared till date. 
                  I request for retirement benefit as per the association rules and regulations.
                </p>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="agree"
                    name="agree"
                    onChange={(e) => formik.setFieldValue('agree', e.target.checked)}
                    className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
                  />
                  <label htmlFor="agree" className="ml-2 text-sm text-gray-700">
                    I confirm that I am 65 years or above and all my dues are cleared <span className="text-red-500">*</span>
                  </label>
                </div>
                {formik.touched.agree && formik.errors.agree && (
                  <p className="mt-1 text-xs text-red-500">{formik.errors.agree}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex justify-end items-center bg-gray-50 p-6 rounded-lg">
              <Button
                variant="primary"
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                className="px-8 py-3 text-lg"
              >
                {isSubmitting ? 'Submitting...' : '📤 Submit Application'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default RetirementBenefitForm;